import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSession } from '../context/SessionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SafeLink from './SafeLink';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './Login.css';

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { sessionRole } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [roleForSignup, setRoleForSignup] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    twoFactorCode: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);

  useEffect(() => {
    // Prioridad 1: SessionContext (desde Landing)
    if (sessionRole) {
      setRoleForSignup(sessionRole.toUpperCase());
      console.log('Login - Role from SessionContext:', sessionRole);
      return;
    }
    
    // Prioridad 2: URL params (fallback)
    const params = new URLSearchParams(location.search);
    const role = params.get('role');
    if (role) {
      setRoleForSignup(role.toUpperCase());
      console.log('Login - Role from URL:', role);
    }
  }, [location, sessionRole]);

  const togglePassword = () => setShowPassword(!showPassword);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getRedirectPath = (userData) => {
    // Check if user has INVESTIGATOR or INSTITUTION role
    const roles = userData.roles || [];
    const roleNames = Array.isArray(roles) 
      ? roles.map(r => typeof r === 'string' ? r : r.name).map(r => r.toUpperCase())
      : [];

    if (roleNames.includes('INVESTIGATOR') || roleNames.includes('INSTITUTION')) {
      return '/feed';
    } else if (roleNames.includes('INTERESTED') || roleNames.includes('PATIENT')) {
      return '/community-page';
    } else {
      return '/community-feed'; // fallback
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password, formData.twoFactorCode);

    if (result.success) {
      if (result.twoFactorRequired) {
        setShow2FA(true);
        setLoading(false);
        return;
      }
      // Get the redirect path based on user roles from the response
      const redirectPath = getRedirectPath(result.user);
      navigate(redirectPath, { state: { from: 'internal' } });
    } else {
      setError(t('auth.invalidCredentials'));
    }
    setLoading(false);
  };

  const getSignupLink = () => {
    if (roleForSignup) {
      return `/signup?role=${roleForSignup}`;
    }
    return '/signup';
  };

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-left-content">
          <img src="/logoecseia.png" alt="Logo" className="login-logo-icon" />
          <h1 className="login-brand-name">
            <span className="brand-eco">Eco</span>ScienceInnova
          </h1>
          <p className="login-tagline">{t('tagline')}</p>
        </div>
        <p className="login-left-footer">© 2026 EcoScienceInnova Labs</p>
      </div>

      <div className="login-right">
        <div className="login-form-wrapper">
          <h2 className="login-title">{t('auth.loginTitle')}</h2>
          <p className="login-subtitle">{t('auth.welcomeBack')}</p>

          <form onSubmit={handleSubmit}>
            <div className="login-field">
              <label className="login-label">{t('auth.email')}</label>
              <input
                className="login-input"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
                required
              />
            </div>

            {!show2FA ? (
              <div className="login-field">
                <div className="login-field-header">
                  <label className="login-label">{t('auth.password')}</label>
                  <SafeLink to="/forgot-password" style={{ color: '#FF6600', fontSize: '0.85rem' }}>
                    {t('auth.forgotPassword')}
                  </SafeLink>
                </div>
                <div className="password-input-wrapper" style={{ position: 'relative' }}>
                  <input
                    className="login-input"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePassword}
                    className="password-toggle-btn"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="login-field">
                <label className="login-label">{t('auth.twoFactorCode')}</label>
                <input
                  className="login-input"
                  type="text"
                  name="twoFactorCode"
                  value={formData.twoFactorCode}
                  onChange={handleChange}
                  placeholder="000000"
                  required
                />
              </div>
            )}

            {error && <div className="login-error" style={{ color: '#ff4444', marginBottom: '1rem' }}>{error}</div>}

            <button type="submit" className="login-btn-submit" disabled={loading}>
              {loading ? '...' : (show2FA ? t('auth.verifyBtn') : t('auth.loginBtn'))}
            </button>
          </form>

          <p className="login-footer-text" style={{ marginTop: '2rem', textAlign: 'center' }}>
            {t('auth.noAccount')}{' '}
            <SafeLink to={getSignupLink()} style={{ color: '#FF6600', fontWeight: 'bold' }}>{t('register')}</SafeLink>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
